/**
 * Records a version of the 215A whenever one of its incident areas changes.
 *
 * The card's only editable content is the area table, and each create, update
 * or delete should leave a draft version behind the way the 202's boxes do.
 * Rather than thread a callback through the table and its dialog, the card
 * provides the recorder here and the area mutation hooks pick it up.
 *
 * The default is a no-op so those hooks keep working outside the card, where
 * there is no operational period to record against.
 */
import React, { createContext, useContext, useMemo } from 'react';

import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

const RecordSafetyAnalysisVersionContext = createContext<() => void>(() => {});

export const useRecordSafetyAnalysisVersion = (): (() => void) =>
  useContext(RecordSafetyAnalysisVersionContext);

export const SafetyAnalysisVersionRecording: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const recordVersion = useRecordFormVersionSave(
    'ICS_215A',
    effectivePeriodNumber
  );

  const record = useMemo(
    () => () => {
      if (!readOnlyForms) recordVersion.mutate();
    },
    [readOnlyForms, recordVersion]
  );

  return (
    <RecordSafetyAnalysisVersionContext.Provider value={record}>
      {children}
    </RecordSafetyAnalysisVersionContext.Provider>
  );
};

export default SafetyAnalysisVersionRecording;
